import React, { useState, useCallback, useEffect } from 'react';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';
import { useFormContext } from '../../context/FormContext';
import { useReadOnly } from '../../hooks/useReadOnly';
import { SignupLayout } from '../ui/SignupLayout';
import { OptionCard } from '../ui/OptionCard';
import { updateQuote } from '../services/QuoteService';
import { SessionExpiredError } from '../services/apiClient';
import GoogleAutoComplete from '../ui/GoogleAutoComplete';

// -- Constants
const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
    'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
    'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
    'VT','VA','WA','WV','WI','WY'
];

const COUNTRIES = ['USA'];

// -- Helper - build a readable owner type label
const OWNER_TYPE_LABELS = {
    INDIVIDUAL: 'Individual',
    SOLE_PROPRIETORSHIP: 'Sole Proprietorship',
    LIMITED_LIABILITY_COMPANY: 'LLC',
    PARTNERSHIP: 'Partnership',
    CORPORATION: 'Corporation',
    S_CORPORATION: 'S-Corporation',
    TRUST: 'Trust',
};

// -- Helper - clear additional party fields from context
const ADDITIONAL_PARTY_FIELDS = {
    AdditionalPartyType: '',
    AdditionalPartyFirstName: '',
    AdditionalPartyLastName: '',
    AdditionalPartyAddressOne: '',
    AdditionalPartyAddressTwo: '',
    AdditionalPartyCity: '',
    AdditionalPartyState: '',
    AdditionalPartyZipCode: '',
    AdditionalPartyCountry: 'USA',
};

export function ConfirmDetails({ history }) {
    const { formData, setField, setFields, setStep } = useFormContext();
    const { isReadOnly, readOnlyProps } = useReadOnly();
    const [errors, setErrors] = useState({});

    // -- Mailing address same-as-property toggle
    const handleSameAsProperty = (checked) => {
        setField('MailingSameAsProperty', checked);
        if (checked) {
            setFields({
                ContactAddressOne: formData.TargetAddressOne || '',
                ContactAddressTwo: formData.TargetAddressTwo || '',
                ContactCity: formData.TargetCity || '',
                ContactState: formData.TargetState || '',
                ContactZipCode: formData.TargetZipCode || '',
                ContactCountry: 'USA',
            });
        } else {
            setFields({
                ContactAddressOne: '',
                ContactAddressTwo: '',
                ContactCity: '',
                ContactState: '',
                ContactZipCode: '',
                ContactCountry: 'USA',
            });
        }
    };

    // -- Initialize same-as-property default on first visit
    // If MailingSameAsProperty hasn't been set yet, default to true
    useEffect(() => {
        if (formData.MailingSameAsProperty === false) return; // explicitly set to false
        if (!formData.ContactAddressOne) {
            handleSameAsProperty(true);
        }
    }, []);

    useEffect(() => {
        setStep('confirmDetails');
    }, []);

    // -- Google Autocomplete for mailing address
    const handleMailingAddress1Change = useCallback((value) => {
        setField('ContactAddressOne', value);
    }, [setField]);

    const handleMailingPlaceSelected = useCallback((place) => {
        if (!place?.address_components) return;
        const comp = place.address_components;
        const get = (type) => comp.find((c) => c.types.includes(type)) || {};

        const streetNumber = get('street_number').long_name || '';
        const route = get('route').long_name || '';
        const city = get('locality').long_name || get('sublocality').long_name || '';
        const state = get('administrative_area_level_1').short_name || '';
        const zip = get('postal_code').long_name || '';
        const sub = get('subpremise').long_name || '';

        setFields({
            ContactAddressOne: `${streetNumber} ${route}`.trim(),
            ContactAddressTwo: sub,
            ContactCity: city,
            ContactState: state,
            ContactZipCode: zip,
            ContactCountry: 'USA',
            MailingSameAsProperty: false,
        });
    }, [setFields]);

    // -- Additional parties toggle
    const handleAdditionalParties = (hasParties) => {
        setField('HasAdditionalParties', hasParties);
        if (!hasParties) {
            // Wipe all additional party data when user selects "No"
            setFields(ADDITIONAL_PARTY_FIELDS);
        }
    };

    // -- Validation
    const validate = () => {
        const e = {};
        if (!formData.MailingSameAsProperty) {
            if (!formData.ContactAddressOne?.trim())
                e.ContactAddressOne = 'Address is required.';
            if (!formData.ContactCity?.trim())
                e.ContactCity = 'City is required.';
            if (!formData.ContactState?.trim())
                e.ContactState = 'State is required.';
            if (!formData.ContactZipCode?.trim())
                e.ContactZipCode = 'ZIP code is required.';
        }
        return e;
    };

    // -- Navigation
    const handleBack = () => history.push('/signup/data-points');

    const handleContinue = async () => {
        if (isReadOnly) {
            if (formData.HasAdditionalParties) {
                history.push('/signup/additional-party');
            } else {
                history.push('/signup/finalize');
            }
            return;
        }
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        setErrors({});

        // Persist current state to DB
        if (formData.QuoteId) {
            try {
                await updateQuote(formData.QuoteId, formData);
            } catch (err) {
                if (err instanceof SessionExpiredError) return;
                console.error('Failed to update quote on continue:', err);
            }
        }

        if (formData.HasAdditionalParties) {
            history.push('/signup/additional-party');
        } else {
            history.push('/signup/finalize');
        }
    };

    // -- Derived display values
    const insuredName = [formData.FirstName, formData.LastName].filter(Boolean).join(' ') || '—';
    const ownerTypeLabel = OWNER_TYPE_LABELS[formData.OwnerType] || formData.OwnerType || '—';
    const propertyAddress = [
        formData.TargetAddressOne,
        formData.TargetAddressTwo,
        formData.TargetCity && formData.TargetState
            ? `${formData.TargetCity}, ${formData.TargetState} ${formData.TargetZipCode}`
            : '',
    ].filter(Boolean).join('\n');

    const mailingAddressDisplay = [
        formData.ContactAddressOne,
        formData.ContactAddressTwo,
        formData.ContactCity && formData.ContactState
            ? `${formData.ContactCity}, ${formData.ContactState} ${formData.ContactZipCode}`
            : '',
    ].filter(Boolean).join('\n');

    return (
        <SignupLayout currentStep={2} totalSteps={3} stepLabel="Confirm details">

            <h1 className="text-h1">Complete your policy holder details.</h1>
            <p className="text-default" style={{marginBottom: `var(--space-lg)`}}>
                Review and confirm your details before we finalize your policy.
            </p>

            {/* POLICY DETAILS - read-only summary card */}
            <div className="summary-card" style={{ marginBottom: 'var(--space-xl)' }}>
                <p className="text-h4--brand" style={{marginBottom: `var(--space-lg)`}}>Policy details</p>

                <div className="summary-row">
                    <span className="text-xs">Insured name</span>
                    <span className="text-default--primary">{insuredName}</span>
                </div>
                <div className="summary-row">
                    <span className="text-xs">Property owner type</span>
                    <span className="text-default--primary">{ownerTypeLabel}</span>
                </div>
                <div className="summary-row">
                    <span className="text-xs">Property address</span>
                    <span className="text-default--primary" style={{ whiteSpace: 'pre-line' }}>
                        {propertyAddress || '—'}
                    </span>
                </div>
                <div className="summary-row">
                    <span className="text-xs">Insured email address</span>
                    <span className="text-default--primary">{formData.UserName || '—'}</span>
                </div>
                <div className="summary-row" style={{ marginBottom: 0 }}>
                    <span className="text-xs">Insured phone number</span>
                    <span className="text-default--primary">{formData.PhoneNumberPrimary || '—'}</span>
                </div>
            </div>

            {/* MAILING ADDRESS */}
            <section className="signup-section">
                <h2 className="text-h3--brand">Confirm your mailing address</h2>
                <p className="text-default" style={{ marginBottom: `var(--space-lg)` }}>
                    We'll send your policy documents and any correspondence here.
                </p>

                {/* Same as property checkbox */}
                <label className="checklist__item" {...readOnlyProps}>
                    <input
                        type="checkbox"
                        checked={!!formData.MailingSameAsProperty}
                        onChange={(e) => handleSameAsProperty(e.target.checked)}
                        className="checklist__input"
                        {...readOnlyProps}
                    />
                    <span className="checklist__box">
                        {formData.MailingSameAsProperty && (
                            <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                                <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </span>
                    <span className="text-small">
                        Mailing address is the same as the insured property address.
                    </span>
                </label>

                {/* If same as property - show read-only summary */}
                {formData.MailingSameAsProperty ? (
                    <div className="summary-card" style={{ marginTop: `var(--space-lg)` }}>
                        <span className="text-xs">Mailing address</span>
                        <br />
                        <span className="text-default--primary" style={{ whiteSpace: 'pre-line' }}>
                            {mailingAddressDisplay || '—'}
                        </span>
                    </div>
                ) : (
                    /* If different - show address form */
                    <div className="mailing-form" style={{ marginTop: `var(--space-lg)` }}>
                        <p className="text-label">
                            <strong>Enter your mailing address.</strong>
                            <span style={{ color: 'var(--error-500)', marginLeft: '2px' }}>*</span>
                        </p>

                        {/* Address 1 - Google Autocomplete */}
                        <div className="form-group">
                            <GoogleAutoComplete
                                value={formData.ContactAddressOne || ''}
                                onChange={handleMailingAddress1Change}
                                onPlaceSelected={handleMailingPlaceSelected}
                                placeholder="Address 1"
                                {...readOnlyProps}
                            />
                            {errors.ContactAddressOne && (
                                <span className="error-text field-error">{errors.ContactAddressOne}</span>
                            )}
                        </div>

                        {/* Address 2 */}
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Address 2"
                                value={formData.ContactAddressTwo || ''}
                                onChange={(e) => setField('ContactAddressTwo', e.target.value)}
                                {...readOnlyProps}
                            />
                        </div>

                        {/* City */}
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="City"
                                value={formData.ContactCity || ''}
                                onChange={(e) => setField('ContactCity', e.target.value)}
                                {...readOnlyProps}
                            />
                            {errors.ContactCity && (
                                <span className="error-text field-error">{errors.ContactCity}</span>
                            )}
                        </div>

                        {/* State */}
                        <div className="form-group">
                            <select
                                value={formData.ContactState || ''}
                                onChange={(e) => setField('ContactState', e.target.value)}
                                {...readOnlyProps}
                            >
                                <option value="">Select state</option>
                                {US_STATES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                            {errors.ContactState && (
                                <span className="error-text field-error">{errors.ContactState}</span>
                            )}
                        </div>

                        {/* ZIP */}
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Zip code"
                                value={formData.ContactZipCode || ''}
                                onChange={(e) => setField('ContactZipCode', e.target.value)}
                                maxLength={10}
                                {...readOnlyProps}
                            />
                            {errors.ContactZipCode && (
                                <span className="error-text field-error">{errors.ContactZipCode}</span>
                            )}
                        </div>

                        {/* Country */}
                        <div className="form-group">
                            <select
                                value={formData.ContactCountry || 'USA'}
                                onChange={(e) => setField('ContactCountry', e.target.value)}
                                {...readOnlyProps}
                            >
                                {COUNTRIES.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </section>

            {/* ADDITIONAL PARTIES */}
            <section className="signup-section">
                <h2 className="text-h3--brand">Confirm insured parties for your policy.</h2>
                <p className="text-default" style={{marginBottom: `var(--space-lg)`}}>
                    Let us know if anyone else should be listed on this policy, such as a co-owner or lienholder.
                </p>

                <div className="dp-options">
                    <OptionCard
                        selected={!formData.HasAdditionalParties}
                        onChange={() => handleAdditionalParties(false)}
                        {...readOnlyProps}
                    >
                        <span>
                            <strong>No,</strong> there are no additional parties to include.
                        </span>
                    </OptionCard>

                    <OptionCard
                        selected={!!formData.HasAdditionalParties}
                        onChange={() => handleAdditionalParties(true)}
                        {...readOnlyProps}
                    >
                        <span>
                            <strong>Yes,</strong> I need to add additional party to the policy.
                        </span>
                    </OptionCard>
                </div>
            </section>

            {/* -- Navigation -- */}
            <div className="signup-nav">
                <button className="btn signup-cta btn--inverse-cta" onClick={handleBack}>
                    <MdArrowBack /> Back
                </button>
                <button className="btn signup-cta" onClick={handleContinue}>
                    Continue <MdArrowForward />
                </button>
            </div>

        </SignupLayout>
    );
}